// Quote-specific formatting and status logic. No DOM, no state, no API.
// Mirrors lib/invoice-format.js: stored statuses are draft / sent / accepted /
// declined / invoiced; "expired" is derived from valid_until the same way
// "overdue" is derived from an invoice's due date.

import { companyToday } from "./company-day.js";
import { daysFromTo } from "../../../shared/company-day.js";
import { formatDocumentDate } from "../../../shared/document-format.js";

export function getQuoteIdFromPath(path) {
  const match = String(path || "").match(/^\/quotes\/([^/]+)$/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

// Label for a "Valid for" option (quotes are always valid for at least a day,
// so there's no 0-day "on receipt" equivalent).
export function formatValidForOption(days) {
  return `${days} days`;
}

export function formatQuoteStatus(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  if (!normalized) return "Draft";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function getQuoteStatusTagClass(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  if (normalized === "accepted") return "app-quote-status-accepted";
  if (normalized === "declined") return "app-quote-status-declined";
  if (normalized === "expired") return "app-quote-status-expired";
  if (normalized === "invoiced") return "app-quote-status-invoiced";
  if (normalized === "sent") return "app-invoice-status-sent";
  return "app-invoice-status-draft";
}

// Takes the quote object. Expiry comes from the server-derived, company-
// timezone `isExpired` flag (see IS_EXPIRED_SQL in server/routes/quotes.js) —
// never from the browser clock. Unlike invoices' derived "overdue" (display
// only), expiry gates whether the client's accept link still works, so the
// app and the public page must agree on which calendar day it flips.
export function getEffectiveQuoteStatus(quote) {
  const normalized = String(quote?.status || "").trim().toLowerCase();
  if (normalized === "sent" && quote?.isExpired) return "expired";
  return normalized || "draft";
}

export function isReadOnlyQuoteStatus(rawStatus) {
  const normalized = String(rawStatus || "").trim().toLowerCase();
  return ["sent", "accepted", "declined", "invoiced"].includes(normalized);
}

// The quote drawer's Valid until field: the day, and how it stands against
// today, as `{ date, timing }`, printed apart as the invoice drawer's Due is
// (`formatDueWithDaysLeft`): the day in the half-width field, the timing under
// it. In one field, "November 29, 2026 (30 days left)" ran past its edge and
// was cut off, and it was in the browser's own date format, where every other
// date in the app is en-GB (30 October 2026). The day is a calendar day, read
// where the reader is (`formatDocumentDate`), never shifted a day west of UTC.
const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})/;

export function formatValidUntilWithDaysLeft(rawValidUntil, isExpired = null, now = new Date()) {
  const day = typeof rawValidUntil === "string" ? CALENDAR_DAY.exec(rawValidUntil.trim()) : null;
  const parsed = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : new Date(rawValidUntil);
  if (rawValidUntil == null || rawValidUntil === "" || Number.isNaN(parsed.getTime())) return { date: "", timing: "" };
  const validUntil = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  // Days from the company's today, the day expiry is decided on, never the browser's.
  const validDay = `${validUntil.getFullYear()}-${String(validUntil.getMonth() + 1).padStart(2, "0")}-${String(validUntil.getDate()).padStart(2, "0")}`;
  const diffDays = daysFromTo(companyToday(now), validDay);
  const date = formatDocumentDate(validUntil);
  // The day count is the company's day, as the server's expiry flag is; a
  // flag read a moment before midnight can still disagree, so the wording
  // is clamped to never contradict the status tag beside it.
  if (isExpired === true && diffDays >= 0) return { date, timing: "Expired" };
  if (isExpired === false && diffDays < 0) return { date, timing: "Expires today" };
  if (diffDays > 1) return { date, timing: `${diffDays} days left` };
  if (diffDays === 1) return { date, timing: "1 day left" };
  if (diffDays === 0) return { date, timing: "Expires today" };
  if (diffDays === -1) return { date, timing: "Expired 1 day ago" };
  return { date, timing: `Expired ${Math.abs(diffDays)} days ago` };
}
